import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import MonitoringMapActions from './MonitoringMapActions';
import L from 'leaflet';
import 'leaflet.heat';
import 'leaflet/dist/leaflet.css';
import './BadSignalClientsMap.css';
import { getMapTileAttribution, getMapTileUrl } from '../../config/mapTiles';
import { getOzmapBoxStyle } from '../../utils/ozmapBoxStyle';
import { buildMonitoringMapData, getMonitoringCtoTooltip, getSignalHeatWeight, getMonitoringSignalLevel, MONITORING_SIGNAL_LEVELS } from '../../utils/networkMonitoringMap';
import { getCtoLabel, getOltLabel } from '../../utils/networkMonitoring';
import { validMapCoordinates } from '../../utils/networkMonitoringMap';
import { monitoringHeatLayer } from '../../utils/monitoringHeatLayer';

const popup = lines => {
    const element = document.createElement('div');
    lines.forEach(line => {
        const row = document.createElement('div');
        row.textContent = line;
        element.appendChild(row);
    });
    return element;
};

const EMPTY_EVENTS = [];
const ctoTooltipOptions = { sticky: true, direction: 'right', offset: [12, 0], className: 'monitoring-cto-tooltip', opacity: 1 };
const ctoTooltip = cto => popup([getMonitoringCtoTooltip(cto)]);
const ctoPopup = (cto, showAllActiveClients) => {
    const element = document.createElement('div');
    element.className = 'monitoring-cto-details';
    const label = document.createElement('span');
    label.className = 'monitoring-cto-label';
    label.textContent = 'Caixa de atendimento';
    const title = document.createElement('strong');
    title.className = 'monitoring-cto-title';
    title.textContent = cto.name;
    const count = document.createElement('p');
    const number = document.createElement('strong');
    number.textContent = String(cto.count);
    count.append(number, showAllActiveClients ? ' cliente(s) com login ativo' : ' cliente(s) com sinal ruim nos resultados filtrados');
    const note = document.createElement('em');
    note.textContent = `${cto.clients.length} drop(s) no mapa${cto.clients.length < cto.count ? ' · Demais clientes sem coordenadas' : ''}`;
    element.append(label, title, count, note);
    return element;
};
const BadSignalClientsMap = ({ clients, ctos = EMPTY_EVENTS, threshold, showAllActiveClients = false, darkMapEnabled = false, onSelectClient, outageEvents = EMPTY_EVENTS, onSelectOutage, expanded = false, focusRequest = null, isAdmin = false, canOpenServiceOrder = isAdmin, theme = 'light' }) => {
    const [menu, setMenu] = useState(null);
    const [mapReady, setMapReady] = useState(false);
    const [updates, setUpdates] = useState({});
    const pendingLocationRef = useRef(null);
    const onUpdated = useCallback((client, result) => {
        if (result.coordinates) pendingLocationRef.current = result.coordinates;
        setUpdates(previous => ({ ...previous, [client.id]: result }));
    }, []);
    const [mapColorOverride, setMapColorOverride] = useState(null);
    const darkTiles = mapColorOverride ?? darkMapEnabled;
    const toggleMapColor = () => setMapColorOverride(!darkTiles);
    useEffect(() => { setMapColorOverride(null); }, [darkMapEnabled]);
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const selectedCtoRef = useRef(null);
    const ctoMarkersRef = useRef(new Map());
    const lastLocatedBoxRef = useRef(null);
    const [locatedBox, setLocatedBox] = useState(null);
    const locateBox = useCallback(box => {
        const coordinates = validMapCoordinates(box.coordinates);
        if (!coordinates) return;
        pendingLocationRef.current = coordinates;
        setLocatedBox({ ...box, coordinates, focusToken: Date.now() });
    }, []);
    const fittedSelectionRef = useRef(null);
    const focusedEventRef = useRef(null);
    const expandedRef = useRef(expanded);
    useEffect(() => {
        // A snapshot can still contain the old position while the IXC edit is being
        // synchronized. Keep the confirmed edit until that client reflects it.
        const byId = new Map(clients.map(client => [String(client.id), client]));
        setUpdates(previous => {
            const entries = Object.entries(previous).filter(([id, result]) => {
                const client = byId.get(id);
                return client && Object.entries(result).some(([key, value]) => key !== 'warning' && JSON.stringify(client[key]) !== JSON.stringify(value));
            });
            return entries.length === Object.keys(previous).length ? previous : Object.fromEntries(entries);
        });
    }, [clients]);
    const data = useMemo(() => buildMonitoringMapData(
        Object.keys(updates).length ? clients.map(client => updates[client.id] ? { ...client, ...updates[client.id] } : client) : clients,
        showAllActiveClients ? ctos : EMPTY_EVENTS,
    ), [clients, updates, ctos, showAllActiveClients]);
    const positions = useMemo(() => [
        ...[...data.clients, ...data.ctos].map(item => [item.coordinates.lat, item.coordinates.lng]),
        ...outageEvents.map(event => validMapCoordinates(event.coordinates)).filter(Boolean).map(coords => [coords.lat, coords.lng]),
    ], [data, outageEvents]);
    // Changes to a client's CTO or coordinates update layers, not the user's viewport.
    // The live Firebase snapshot creates new objects, so compare stable IDs instead.
    const selectionSignature = useMemo(() => JSON.stringify([
        clients.filter(client => client.loginActive === true).map(client => String(client.id)).sort(),
        outageEvents.map(event => String(event.id)).sort(),
        showAllActiveClients ? ctos.map(cto => String(cto.id)).sort() : [],
    ]), [clients, outageEvents, ctos, showAllActiveClients]);

    useEffect(() => {
        // Separate full-size canvases let the CTO canvas intercept events over clients.
        // SVG renderers leave empty space transparent to pointer events in each pane.
        const map = L.map(containerRef.current, { preferCanvas: false, scrollWheelZoom: true, zoomSnap: 0.5, zoomDelta: 0.5, wheelPxPerZoomLevel: 60 });
        mapRef.current = map;
        setMapReady(true);
        map.setView([-22.5, -48.5], 7);
        const connectionsPane = map.createPane('monitoringConnections');
        connectionsPane.style.zIndex = 410;
        connectionsPane.style.pointerEvents = 'none';
        map.createPane('monitoringClients').style.zIndex = 610;
        map.createPane('monitoringCtos').style.zIndex = 620;
        map.createPane('monitoringOutages').style.zIndex = 650;
        const observer = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => map.invalidateSize({ animate: false })) : null;
        observer?.observe(containerRef.current);
        return () => { observer?.disconnect(); map.remove(); mapRef.current = null; };
    }, []);

    useEffect(() => {
        const tiles = L.tileLayer(getMapTileUrl(darkTiles), {
            attribution: getMapTileAttribution(darkTiles), maxZoom: 22, maxNativeZoom: 19, crossOrigin: true,
        }).addTo(mapRef.current);
        return () => tiles.remove();
    }, [darkTiles]);

    useEffect(() => {
        const map = mapRef.current;
        const layer = L.layerGroup().addTo(map);
        outageEvents.forEach(event => {
            const coords = validMapCoordinates(event.coordinates);
            if (!coords) return;
            const confirmed = event.status === 'confirmed';
            L.marker([coords.lat, coords.lng], {
                pane: 'monitoringOutages',
                zIndexOffset: 1000,
                icon: L.divIcon({ className: '', html: `<div class="${confirmed ? 'outage-epicenter' : 'outage-warning'}">⚠</div>`, iconSize: [40, 40], iconAnchor: [20, 20] }),
                title: `${event.ctoName}: ${confirmed ? 'Rompimento confirmado' : 'Suspeita de rompimento'}`,
            }).bindTooltip(popup([event.ctoName, `${event.affectedClients}/${event.totalClients} clientes offline`, confirmed ? 'Confirmado' : 'Suspeita']))
                .on('click', () => { setMenu(null); onSelectOutage?.(event.id); }).addTo(layer);
        });
        return () => layer.remove();
    }, [outageEvents, onSelectOutage]);

    // Heat density uses the full inventory and does not need marker or popup rebuilds.
    useEffect(() => {
        const heat = monitoringHeatLayer(data.clients.map(client => [client.coordinates.lat, client.coordinates.lng, getSignalHeatWeight(client.signal, threshold)]), {
            radius: 30, blur: 18, maxZoom: 16, minOpacity: 0.25,
            gradient: { 0.2: '#3b82f6', 0.45: '#22c55e', 0.65: '#facc15', 0.85: '#f97316', 1: '#ef4444' },
        }).addTo(mapRef.current);
        heat._canvas.style.pointerEvents = 'none';
        heat._canvas.style.zIndex = '0';
        return () => heat.remove();
    }, [data, threshold]);

    useEffect(() => {
        const map = mapRef.current;
        const clientLayer = L.layerGroup().addTo(map);
        const ctoLayer = L.layerGroup().addTo(map);
        const connectionsLayer = L.layerGroup().addTo(map);
        const clientMarkers = new Map();
        const ctoMarkers = new Map();
        // Retain lightweight marker objects, but only attach paths in the viewport.
        // A margin avoids churn along the edges during small pans and fractional zooms.
        const refreshVisibleMarkers = () => {
            const size = map.getSize();
            const bounds = size.x && size.y ? map.getBounds().pad(0.2) : null;
            const sync = (markers, layer) => markers.forEach(marker => {
                const visible = !bounds || bounds.contains(marker.getLatLng()) || marker.isPopupOpen();
                if (visible && !layer.hasLayer(marker)) layer.addLayer(marker);
                else if (!visible && layer.hasLayer(marker)) layer.removeLayer(marker);
            });
            sync(clientMarkers, clientLayer);
            sync(ctoMarkers, ctoLayer);
        };
        let disposing = false;
        let activeCto = null;
        let hoveredCto = null;
        const drawCtoConnections = () => {
            connectionsLayer.clearLayers();
            (hoveredCto || activeCto)?.clients.forEach(client => drawConnection(hoveredCto || activeCto, client));
        };
        const clearHover = () => {
            hoveredCto = null;
            drawCtoConnections();
            clientMarkers.forEach(marker => marker.closeTooltip());
        };
        const closeSelection = () => {
            activeCto = null;
            hoveredCto = null;
            selectedCtoRef.current = null;
            connectionsLayer.clearLayers();
            clientMarkers.forEach(marker => marker.closeTooltip());
            ctoLayer.eachLayer(marker => marker.closePopup());
        };
        const backgroundClick = event => {
            if (event.propagatedFrom || (event.sourceTarget && event.sourceTarget !== map)) return;
            if (event.originalEvent?.target?.closest?.('.leaflet-interactive, .leaflet-marker-icon, .leaflet-control, .leaflet-popup')) return;
            closeSelection();
        };
        const drawConnection = (cto, client) => L.polyline([
            [cto.coordinates.lat, cto.coordinates.lng], [client.coordinates.lat, client.coordinates.lng],
        ], {
            pane: 'monitoringConnections', color: '#3b82f6', weight: 2,
            opacity: 0.85, dashArray: '6 6', interactive: false,
        }).addTo(connectionsLayer);
        const ctoByClient = new Map();
        data.ctos.forEach(cto => cto.clients.forEach(client => ctoByClient.set(client, cto)));
        map.on('zoomstart movestart mouseout', clearHover);
        map.on('click', backgroundClick);
        data.clients.forEach(client => {
            const marker = L.circleMarker([client.coordinates.lat, client.coordinates.lng], {
                radius: 5, color: '#fff', weight: 1, fillColor: getMonitoringSignalLevel(client.signal, threshold).color, fillOpacity: 1, pane: 'monitoringClients', bubblingMouseEvents: false,
            }).bindTooltip(() => popup([client.name || `Cliente ${client.id}`]), { direction: 'top', className: 'monitoring-client-tooltip', opacity: 0.95 }).bindPopup(() => {
                const content = popup([client.name || `Cliente ${client.id}`, `Endereço: ${client.address || 'Não informado'}`, `CTO vinculada: ${getCtoLabel(client)}`, `Rx: ${client.signal ?? 'N/A'} dBm`, `OLT: ${getOltLabel(client)}`]);
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'text-blue-600 underline mt-2';
                button.textContent = 'Ver cliente';
                button.onclick = () => onSelectClient?.(client);
                if (onSelectClient) content.appendChild(button);
                return content;
            });
            marker.on('contextmenu', event => {
                if (event.originalEvent) { L.DomEvent.preventDefault(event.originalEvent); L.DomEvent.stopPropagation(event.originalEvent); }
                if (!isAdmin) { setMenu(null); return; }
                const point = event.containerPoint || map.latLngToContainerPoint(marker.getLatLng());
                setMenu({ client, x: Math.max(0, Math.min(point.x, containerRef.current.clientWidth - 224)), y: Math.max(0, Math.min(point.y, containerRef.current.clientHeight - 160)) });
            });
            marker.on('click', () => setMenu(null));
            marker.on('mouseover', () => {
                clearHover();
                marker.openTooltip();
                const cto = ctoByClient.get(client);
                if (cto && !activeCto) drawConnection(cto, client);
            }).on('mouseout', clearHover);
            clientMarkers.set(client, marker);
        });
        data.ctos.forEach(cto => {
            const position = [cto.coordinates.lat, cto.coordinates.lng];
            const marker = L.circleMarker(position, {
                ...getOzmapBoxStyle({ name: 'CTO' }), pane: 'monitoringCtos', bubblingMouseEvents: false,
            })
                .bindTooltip(() => ctoTooltip(cto), ctoTooltipOptions)
                .bindPopup(() => ctoPopup(cto, showAllActiveClients), { className: 'monitoring-cto-popup', maxWidth: 260, autoPan: false })
                .on('click', () => setMenu(null))
                .on('contextmenu', event => {
                    if (event.originalEvent) { L.DomEvent.preventDefault(event.originalEvent); L.DomEvent.stopPropagation(event.originalEvent); }
                    if (!canOpenServiceOrder || !cto.id) { setMenu(null); return; }
                    const point = event.containerPoint || map.latLngToContainerPoint(marker.getLatLng());
                    setMenu({ box: cto, x: Math.max(0, Math.min(point.x, containerRef.current.clientWidth - 224)), y: Math.max(0, Math.min(point.y, containerRef.current.clientHeight - 100)) });
                })
                .on('mouseover', () => {
                    hoveredCto = cto;
                    drawCtoConnections();
                })
                .on('mouseout', () => {
                    hoveredCto = null;
                    drawCtoConnections();
                })
                .on('popupopen', () => {
                    activeCto = cto;
                    selectedCtoRef.current = cto.key;
                    drawCtoConnections();
                })
                .on('popupclose', () => {
                    if (!disposing && activeCto?.key === cto.key) {
                        activeCto = null;
                        selectedCtoRef.current = null;
                        drawCtoConnections();
                    }
                });
            ctoMarkers.set(cto.key, marker);
        });
        refreshVisibleMarkers();
        map.on('moveend zoomend resize', refreshVisibleMarkers);
        ctoMarkersRef.current = ctoMarkers;
        if (selectedCtoRef.current && ctoMarkers.has(selectedCtoRef.current)) ctoMarkers.get(selectedCtoRef.current).openPopup();
        else selectedCtoRef.current = null;
        return () => {
            disposing = true;
            map.off('zoomstart movestart mouseout', clearHover);
            map.off('click', backgroundClick);
            map.off('moveend zoomend resize', refreshVisibleMarkers);
            connectionsLayer.remove();
            clientLayer.remove(); ctoLayer.remove();
        };
    }, [data, threshold, onSelectClient, isAdmin, canOpenServiceOrder, showAllActiveClients]);

    useEffect(() => {
        if (!locatedBox) return undefined;
        const newFocus = lastLocatedBoxRef.current !== locatedBox;
        lastLocatedBoxRef.current = locatedBox;
        const existing = ctoMarkersRef.current.get(`id:${locatedBox.id}`);
        if (existing) { if (newFocus) existing.openPopup(); return undefined; }
        const marker = L.circleMarker([locatedBox.coordinates.lat, locatedBox.coordinates.lng], {
            ...getOzmapBoxStyle({ name: 'CTO' }), pane: 'monitoringCtos', bubblingMouseEvents: false,
        }).bindTooltip(() => ctoTooltip(locatedBox), ctoTooltipOptions).bindPopup(popup([locatedBox.name || `CTO ${locatedBox.id}`, 'CTO localizada fora dos resultados exibidos.']), { autoPan: false }).on('contextmenu', event => {
            if (event.originalEvent) { L.DomEvent.preventDefault(event.originalEvent); L.DomEvent.stopPropagation(event.originalEvent); }
            if (!canOpenServiceOrder || !locatedBox.id) { setMenu(null); return; }
            const point = event.containerPoint || mapRef.current.latLngToContainerPoint(marker.getLatLng());
            setMenu({ box: locatedBox, x: Math.max(0, Math.min(point.x, containerRef.current.clientWidth - 224)), y: Math.max(0, Math.min(point.y, containerRef.current.clientHeight - 100)) });
        }).addTo(mapRef.current);
        if (newFocus) marker.openPopup();
        return () => marker.remove();
    }, [locatedBox, data, threshold, onSelectClient, outageEvents, isAdmin, canOpenServiceOrder, showAllActiveClients]);

    // Run after marker rendering and portal layout, so expansion uses the actual viewport size.
    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            const map = mapRef.current;
            if (!map) return;
            const resized = expandedRef.current !== expanded;
            const center = map.getCenter();
            const zoom = map.getZoom();
            map.invalidateSize({ animate: false });
            if (resized) map.setView(center, zoom, { animate: false });
            const focusEvent = outageEvents.find(event => event.id === focusRequest?.id);
            const focus = validMapCoordinates(focusEvent?.coordinates);
            const focusKey = focusRequest ? `${focusRequest.id}:${focusRequest.focusToken || 0}` : null;
            const location = validMapCoordinates(pendingLocationRef.current);
            if (location) {
                map.setView([location.lat, location.lng], 17, { animate: false });
                pendingLocationRef.current = null;
                fittedSelectionRef.current = selectionSignature;
                focusedEventRef.current = focusKey;
            } else if (focus && focusedEventRef.current !== focusKey) {
                map.setView([focus.lat, focus.lng], 17, { animate: false });
                focusedEventRef.current = focusKey;
                fittedSelectionRef.current = selectionSignature;
            } else if (positions.length && selectionSignature !== fittedSelectionRef.current) {
                map.fitBounds(L.latLngBounds(positions), { padding: [50, 50], maxZoom: 17, animate: false });
                fittedSelectionRef.current = selectionSignature;
            }
            expandedRef.current = expanded;
        });
        return () => cancelAnimationFrame(frame);
    }, [positions, selectionSignature, expanded, focusRequest, outageEvents, locatedBox]);

    const locatedBoxOutsideResults = locatedBox && !data.ctos.some(cto => cto.key === `id:${locatedBox.id}`);
    const hasPositions = data.clients.length > 0 || data.ctos.length > 0 || locatedBoxOutsideResults || outageEvents.some(event => validMapCoordinates(event.coordinates));
    return (
        <section aria-label="Mapa de monitoramento" className="monitoring-map-section p-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 text-sm text-slate-600 dark:text-slate-300">
                <h4 className="font-semibold">Mapa — {data.clients.length} cliente(s) e {data.ctos.length + (locatedBoxOutsideResults ? 1 : 0)} CTO(s)</h4>
                <span>● Clientes <span className="ml-3 text-yellow-500">●</span> CTOs</span>
            </div>
            {showAllActiveClients && <div aria-label="Legenda de sinal dos clientes" className="flex flex-wrap gap-3 mb-3 text-xs text-slate-600 dark:text-slate-300">
                {MONITORING_SIGNAL_LEVELS.map(level => <span key={level.label}><span style={{ color: level.color }}>●</span> {level.label}</span>)}
                <span>Crítico: ≤ −30 dBm · Ruim: abaixo do threshold · Bom: até −24 dBm · Ótimo: −24 a −20 dBm · Excelente: ≥ −20 dBm</span>
            </div>}
            <div className="monitoring-map-frame relative">
                <div ref={containerRef} className="monitoring-map-canvas h-[420px] w-full rounded-lg z-0" />
                {mapReady && <MonitoringMapActions mapRef={mapRef} menu={menu} setMenu={setMenu} theme={theme} onUpdated={onUpdated} onLocateBox={locateBox} onToggleMapColor={toggleMapColor} isAdmin={isAdmin} canOpenServiceOrder={canOpenServiceOrder} />}
                {!hasPositions && <div role="status" className="absolute inset-0 z-10 pointer-events-none flex items-center justify-center rounded-lg bg-slate-100/80 dark:bg-slate-700/80 p-6 text-center text-slate-600 dark:text-slate-200">
                    {clients.some(client => client.loginActive === true) ? 'Coordenadas indisponíveis. Atualize o Monitoramento para carregar as posições dos clientes e CTOs.' : 'Nenhum cliente com login ativo nos resultados. Atualize o Monitoramento para consultar o IXC.'}
                </div>}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                <span>Heatmap: concentração de clientes ponderada pela piora do Rx em relação ao threshold.</span>
                <span className="inline-block h-2 w-24 rounded" style={{ background: 'linear-gradient(to right, #3b82f6, #22c55e, #facc15, #f97316, #ef4444)' }} />
                <span>Menor → maior intensidade</span>
            </div>
            {(data.missingClients > 0 || data.missingCtos > 0) && <p role="status" className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                Sem coordenadas válidas: {data.missingClients} cliente(s) e {data.missingCtos} CTO(s). {showAllActiveClients ? 'Esses registros não podem ser posicionados no mapa.' : 'Esses registros continuam na tabela.'}
            </p>}
        </section>
    );
};

export default React.memo(BadSignalClientsMap);
